import React, { useState, useEffect } from 'react';
import { useInput, useRecordContext, useDataProvider } from 'react-admin';
import { getAudioDuration } from '../../utils/audio';

interface AudioFileInputProps {
  source: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  validate?: any[];
  [key: string]: any;
}

export const AudioFileInput = ({ source, ...props }: AudioFileInputProps) => {
  const { field } = useInput({ source });
  const durationField = useInput({ source: 'duration_seconds' });
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [isLoadingDuration, setIsLoadingDuration] = useState(false);
  const [fileWasRemoved, setFileWasRemoved] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);


  useEffect(() => {
    if (record && record.id && !isInitialized && !fileWasRemoved) {
      setIsInitialized(true);
      dataProvider.getAudioUrl(record.id)
          .then((url: string) => {

            const virtualFile = new File([], 'current_audio.mp3', { type: 'audio/mpeg' });
            if (record.file_size) {
              Object.defineProperty(virtualFile, 'size', { value: record.file_size, writable: false });
            }
            field.onChange({ 
              rawFile: virtualFile, 
              src: url,
              isExisting: true // Флаг что это существующий файл
            });
            if (record.duration_seconds) {
              durationField.field.onChange(record.duration_seconds);
            }
          })
          .catch((err: any) => {
            console.error('Failed to load current audio:', err instanceof Error ? err.message : 'Unknown error');
          });
    }
  }, [record, dataProvider, field, durationField, isInitialized, fileWasRemoved]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileWasRemoved(false);
      // Set file
      field.onChange({ rawFile: file, src: URL.createObjectURL(file) });
      setIsUploading(true);
      setUploadProgress(0);

      // Simulate progress for file processing
      const simulateProgress = () => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += Math.random() * 15;
          if (progress >= 100) {
            progress = 100;
            clearInterval(interval);
          }
          setUploadProgress(Math.min(progress, 100));
        }, 100);
        return interval;
      };
      
      const progressInterval = simulateProgress();
      
      // Determine duration
      setIsLoadingDuration(true);
      try {
        const duration = await getAudioDuration(file);
        durationField.field.onChange(duration);
        setUploadProgress(100);
      } catch (error) {
        console.error('Failed to get audio duration:', error instanceof Error ? error.message : 'Unknown error');
      } finally {
        clearInterval(progressInterval);
        setTimeout(() => {
          setIsLoadingDuration(false);
          setIsUploading(false);
          setUploadProgress(0);
        }, 500);
      }
    } else {
      field.onChange(null);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div>
        <label 
          htmlFor="audio-file-create-input"
          style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}
        >
          Audio file:
        </label>
      <input
            id="audio-file-create-input"
        type="file"
        accept="audio/mpeg,audio/mp3,audio/mp4,audio/m4a"
        onChange={handleFileChange}
        {...props}
      />
      
      {/* Progress bar for file processing */}
      {isUploading && (
        <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <div style={{ marginBottom: '4px', fontSize: '14px', color: '#666' }}>
            Processing audio file...
          </div>
          <div style={{ 
            width: '100%', 
            height: '8px', 
            backgroundColor: '#e0e0e0', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${uploadProgress}%`,
              height: '100%',
              backgroundColor: '#4caf50',
              transition: 'width 0.3s ease',
              borderRadius: '4px'
            }} />
          </div>
          <div style={{ marginTop: '4px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
            {uploadProgress}%
          </div>
        </div>
      )}
      
      {isLoadingDuration && <div>Determining duration...</div>}
      {field.value && (field.value.isExisting || field.value.src || field.value.rawFile) ? (
        <div style={{ marginTop: '8px', position: 'relative' }}>
          {/* Audio preview */}
          <div style={{ marginBottom: '8px' }}>
            <audio controls style={{ width: '100%', height: '40px' }}>
              <source src={field.value.src} type="audio/mpeg" />
              <source src={field.value.src} type="audio/mp4" />
              <source src={field.value.src} type="audio/m4a" />
              Your browser does not support the audio element.
            </audio>
          </div>
          
          {/* File info */}
          <div style={{ marginBottom: '8px' }}>
            <div>File: {field.value.isExisting ? (record?.title || 'Current audio file') : field.value.rawFile?.name}</div>
            {field.value.rawFile?.size && field.value.rawFile.size > 0 && (
              <div>Size: {(field.value.rawFile.size / 1024 / 1024).toFixed(2)} MB</div>
            )}
            {durationField.field.value && (
              <div>Duration: {Math.floor(durationField.field.value / 60)}:{(durationField.field.value % 60).toString().padStart(2, '0')}</div>
            )}
          </div>
          
          {/* Remove button */}
          <button
            onClick={() => {
              setFileWasRemoved(true);
              field.onChange(null);
              durationField.field.onChange(null);
              // Clear the file input
              const fileInput = document.getElementById('audio-file-create-input') as HTMLInputElement;
              if (fileInput) {
                fileInput.value = '';
              }
            }}
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              background: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Remove audio file"
          >
            ×
          </button>
        </div>
      ) : (
        <div style={{ marginTop: '8px', padding: '8px', color: '#d32f2f', fontStyle: 'italic', fontWeight: '500' }}>
          Select Audio File (required)
        </div>
      )}
    </div>
  );
};

